/* GRN Datewise Purchase Report — API integration.
 * Base: /pharmacy-ims/api/v1/reports  (httpSupplier already targets
 * REACT_APP_SUPPLIER_API_BASE_URL = …/pharmacy-ims/api/, so paths are v1/reports/…).
 *
 *   GET  v1/reports/grn?warehouseId=&supplierId=&status=&medicineCode=&batchNo=
 *                       &fromDate=&toDate=&search=&page=&size=
 *   POST v1/reports/grn/export  { …same filters }   -> a PENDING task
 *   GET  v1/reports/tasks/{taskId}                  -> poll until COMPLETED / FAILED
 *   GET  v1/reports/tasks/{taskId}/download         -> CSV blob
 *
 * The response envelope, the 45-day window rule and the export-task endpoints are
 * common to every item-level report — see reportApiShared.js.
 *
 * Ordering is fixed server-side (GRN date, newest first) — the report is an
 * item-level projection whose ORDER BY lives in the query, so no `sort` param. */
import { httpSupplier } from "../../HttpCommonALL";
import { normalizeTask, num, reportTasks, rethrow, unwrap, xUser } from "./reportApiShared";

const BASE = "v1/reports/grn";

/* Build the shared filter object sent as query params (list) or JSON body
   (export). Blank values and the "ALL" status sentinel are dropped so the
   backend treats them as unset. */
export const toQuery = (filters = {}) => {
  const q = {};
  if (filters.warehouseId) q.warehouseId = String(filters.warehouseId).trim();
  if (filters.supplierId) q.supplierId = Number(filters.supplierId);
  if (filters.status && filters.status !== "ALL") q.status = filters.status;
  if (filters.medicineCode) q.medicineCode = filters.medicineCode.trim();
  if (filters.batchNo) q.batchNo = filters.batchNo.trim();
  if (filters.fromDate) q.fromDate = filters.fromDate;
  if (filters.toDate) q.toDate = filters.toDate;
  if (filters.search) q.search = filters.search.trim();
  return q;
};

/* Map a GrnReportRow to the UI shape. The row is one invoice line item plus its
   invoice / supplier / warehouse context; everything arithmetic (margin, item
   cost, MRP values, discount roll-up) is already derived server-side, so this is
   a rename-and-coerce pass with no money math. */
const fromRow = (r = {}) => ({
  /* header */
  invoiceId: r.invoiceId ?? null,
  invoiceNumber: r.invoiceNumber ?? "",
  invoiceDate: r.invoiceDate ?? "",
  receivedDate: r.receivedDate ?? "",
  status: (r.status ?? "").toString().toUpperCase(),
  poNumber: r.poNumber ?? "",
  billDiscountAmount: num(r.billDiscountAmount),
  /* supplier / warehouse */
  supplierId: r.supplierId ?? null,
  supplierName: r.supplierName ?? "",
  warehouseId: r.warehouseId ?? "",
  warehouseName: r.warehouseName ?? "",
  warehouseCode: r.warehouseCode ?? "",
  region: r.region ?? "",
  /* product */
  medicineCode: r.medicineCode ?? "",
  medicineName: r.medicineName ?? "",
  drugCategory: r.drugCategory ?? "",
  hsnCode: r.hsnCode ?? "",
  batchNo: r.batchNo ?? "",
  expiryDate: r.expiryDate ?? "",
  /* quantities — strips (packs) as billed, plus the loose-unit equivalents the
     legacy extract's Quantity columns show */
  quantityOrdered: num(r.quantityOrdered),
  quantityReceived: num(r.quantityReceived),
  freeQuantity: num(r.freeQuantity),
  unitsPerStrip: num(r.unitsPerStrip),
  quantityOrderedUnits: num(r.quantityOrderedUnits),
  quantityReceivedUnits: num(r.quantityReceivedUnits),
  freeQuantityUnits: num(r.freeQuantityUnits),
  diffQuantityUnits: num(r.diffQuantityUnits),
  /* rates */
  purchasePrice: num(r.purchasePrice),
  mrp: num(r.mrp),
  sellingPrice: num(r.sellingPrice),
  itemCost: num(r.itemCost),
  /* discounts */
  schemeDiscountAmount: num(r.schemeDiscountAmount),
  discountAmount: num(r.discountAmount),
  discountPercentage: num(r.discountPercentage),
  productLevelDiscountAmount: num(r.productLevelDiscountAmount),
  productLevelDiscountPercentage: num(r.productLevelDiscountPercentage),
  /* tax / totals */
  gstPercentage: num(r.gstPercentage),
  taxDescription: r.taxDescription ?? "",
  grossAmount: num(r.grossAmount),
  taxableAmount: num(r.taxableAmount),
  cgstAmount: num(r.cgstAmount),
  sgstAmount: num(r.sgstAmount),
  igstAmount: num(r.igstAmount),
  utgstAmount: num(r.utgstAmount),
  gstAmount: num(r.gstAmount),
  lineTotal: num(r.lineTotal),
  /* MRP / margin */
  totalMrpValue: num(r.totalMrpValue),
  freeMrpValue: num(r.freeMrpValue),
  mrpNetOfGst: num(r.mrpNetOfGst),
  stockValueAtCost: num(r.stockValueAtCost),
  margin: num(r.margin),
  marginPercentage: num(r.marginPercentage),
});

const grnReportService = {
  /* GET v1/reports/grn -> paged GRN line items, newest GRN date first. */
  list(filters = {}, { page = 0, size = 20 } = {}) {
    return httpSupplier
      .get(BASE, { params: { ...toQuery(filters), page, size }, headers: xUser() })
      .then((r) => {
        const data = unwrap(r);
        const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
        return {
          content: content.map(fromRow),
          totalPages: data?.totalPages ?? (content.length ? 1 : 0),
          totalElements: data?.totalElements ?? content.length,
          page: data?.number ?? page,
          size: data?.size ?? size,
        };
      })
      .catch(rethrow);
  },

  /* POST v1/reports/grn/export -> start CSV generation (the legacy
     "PurchaseReportGRNDatewise Details" column layout). Returns the normalized
     task so the caller can poll by taskId and name the download with the
     server-provided fileName. */
  requestExport(filters = {}) {
    return httpSupplier
      .post(`${BASE}/export`, toQuery(filters), { headers: xUser() })
      .then((r) => normalizeTask(unwrap(r)))
      .catch(rethrow);
  },

  /* Task polling / download are report-type-agnostic. */
  exportStatus: reportTasks.status,
  download: (taskId, filename = "grn-report.csv") => reportTasks.download(taskId, filename),
};

export default grnReportService;
